import type {
  CollapseAnimState,
  GamePhase,
  RotationAnimState,
  RotationDir,
  RunState,
  SwapAnimState
} from "./types";
import type { BotDecisionDiagnostics } from "../versus/botInputAdapter";

export interface GameplayDebugSnapshot {
  phase: GamePhase;
  timers: {
    doomClockSec: number;
    timeToNextRotationSec: number;
    doomFrozen: boolean;
    rotationAnimRemainingSec: number;
    collapseAnimRemainingSec: number;
    swapAnimRemainingSec: number;
  };
  scoreFlow: {
    runTotalScore: number;
    displayScore: number;
    displayDelta: number;
    levelScore: number;
    chainDepth: number;
  };
  actionQueues: {
    queuedSwaps: number;
    pendingScoreBatches: number;
    deferredCollapseSteps: number;
    pendingPostResolveChecks: boolean;
  };
  transitions: {
    pendingRotation: RotationDir | null;
    warningActive: boolean;
    isAnimating: boolean;
    rotationPhase: RotationAnimState["phase"] | null;
    collapseStepIndex: number | null;
    powerupBusy: boolean;
  };
  botDiagnostics: BotDecisionDiagnostics | null;
}

export interface GameplayDebugSnapshotInput {
  phase: GamePhase;
  run: RunState | null;
  displayScore: number;
  currentChainDepth: number;
  warningActive: boolean;
  pendingRotation: RotationDir | null;
  queuedSwapsCount: number;
  pendingScoreBatchesCount: number;
  deferredCollapseStepsCount: number;
  pendingPostResolveChecks: boolean;
  rotationAnim: RotationAnimState | null;
  collapseAnim: CollapseAnimState | null;
  swapAnim: SwapAnimState | null;
  powerupBusy: boolean;
  doomFrozenByPowerupAnim: boolean;
  botDiagnostics: BotDecisionDiagnostics | null;
}

function remaining(elapsedSec: number, durationSec: number): number {
  return Math.max(0, durationSec - elapsedSec);
}

function hasActiveVisualAnimation(input: GameplayDebugSnapshotInput): boolean {
  return input.rotationAnim?.active === true
    || input.collapseAnim?.active === true
    || input.swapAnim?.active === true;
}

export function buildGameplayDebugSnapshot(input: GameplayDebugSnapshotInput): GameplayDebugSnapshot | null {
  const run = input.run;
  if (run == null) {
    return null;
  }

  const rotationAnimRemainingSec = input.rotationAnim == null
    ? 0
    : remaining(input.rotationAnim.elapsedSec, input.rotationAnim.phaseDurationSec);
  const collapseAnimRemainingSec = input.collapseAnim == null
    ? 0
    : remaining(input.collapseAnim.elapsedSec, input.collapseAnim.durationSec);
  const swapAnimRemainingSec = input.swapAnim == null
    ? 0
    : remaining(input.swapAnim.elapsedSec, input.swapAnim.durationSec);
  const doomFrozen = run.groove.active || hasActiveVisualAnimation(input) || input.doomFrozenByPowerupAnim;

  return {
    phase: input.phase,
    timers: {
      doomClockSec: run.doomClockSec,
      timeToNextRotationSec: run.beat.timeToNextRotationSec,
      doomFrozen,
      rotationAnimRemainingSec,
      collapseAnimRemainingSec,
      swapAnimRemainingSec
    },
    scoreFlow: {
      runTotalScore: run.totalScore,
      displayScore: input.displayScore,
      displayDelta: input.displayScore - run.totalScore,
      levelScore: run.levelScore,
      chainDepth: input.currentChainDepth
    },
    actionQueues: {
      queuedSwaps: input.queuedSwapsCount,
      pendingScoreBatches: input.pendingScoreBatchesCount,
      deferredCollapseSteps: input.deferredCollapseStepsCount,
      pendingPostResolveChecks: input.pendingPostResolveChecks
    },
    transitions: {
      pendingRotation: input.pendingRotation,
      warningActive: input.warningActive,
      isAnimating: hasActiveVisualAnimation(input),
      rotationPhase: input.rotationAnim?.phase ?? null,
      collapseStepIndex: input.collapseAnim?.stepIndex ?? null,
      powerupBusy: input.powerupBusy
    },
    botDiagnostics: input.botDiagnostics
  };
}
